import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'src/utils/axios';
import type { RootState } from 'src/redux/rootReducerType';
import type {
  CertificationDocument,
  TemplateDocument,
} from 'src/types/CertificationDocumentTypes';
import type {
  HouseholdMemberOption,
  HouseholdMemberResponse,
} from 'src/types/SignatureTypes';
import type { AppDispatch } from 'src/redux/store';
import { applyDisplayOrder } from 'src/components/Compliance/Certification/FileManagerV3/utils/searchUtils';
import {
  convertNestedToOrderData,
  OrderDataItem,
  buildOrderedPackageDocuments,
} from 'src/components/Compliance/Certification/FileManagerV3/utils/FileManagerV3Utils';
import { upsertDocumentsIntoTree } from 'src/components/Compliance/Certification/FileManagerV3/utils/documentTreeUtils';

export interface CertificationFile {
  id: number;
  title: string;
  is_generating: boolean;
  file: string | null;
  documents: number[];
  has_new_logs: boolean;
}

export interface FileManagerV3State {
  // Core state
  loading: boolean;
  isInitialLoad: boolean;

  // Data state
  certificationDocuments: CertificationDocument[];
  certificationFile: CertificationFile | null;
  householdId: number | null;
  householdMembers: HouseholdMemberOption[];
  selectedPrograms: string[];
  isReordering: boolean;

  // Permissions
  canViewBlockerDetails: boolean;
  canAccessFormDebug: boolean;

  // Selection state
  selectedDocs: CertificationDocument[];

  // Search and filtering
  search: string | null;
  renderedDocuments: CertificationDocument[];

  // Operations state
  downloading: boolean;
  previewing: boolean;
  previewOpen: boolean;
  previewDocument: CertificationDocument | TemplateDocument | null;
  deleting: boolean;
  deleteConfirmationOpen: boolean;
  documentsToDelete: CertificationDocument[];
  isOrdering: boolean;
  isSaving: boolean;

  // UI state
  showClearSearch: boolean;
  isFullscreen: boolean;

  activeDragId: string | null;
  overDropId: string | null;
  offsetLeft: number;
  projected: {
    depth: number;
    parentId: string | null;
  } | null;

  // Deleted Documents State
  deletedDocuments: CertificationDocument[];

  // Templates State
  templates: TemplateDocument[];
  loadingTemplates: boolean;
}

const initialState: FileManagerV3State = {
  loading: false,
  isInitialLoad: true,
  certificationDocuments: [],
  certificationFile: null,
  householdId: null,
  householdMembers: [],
  selectedPrograms: [],
  isReordering: false,
  canViewBlockerDetails: false,
  canAccessFormDebug: false,
  selectedDocs: [],
  search: null,
  renderedDocuments: [],
  downloading: false,
  previewing: false,
  previewOpen: false,
  previewDocument: null,
  deleting: false,
  deleteConfirmationOpen: false,
  documentsToDelete: [],
  isOrdering: false,
  isSaving: false,

  showClearSearch: false,
  isFullscreen: false,

  activeDragId: null,
  overDropId: null,
  offsetLeft: 0,
  projected: null,

  // Deleted Documents Initial State
  deletedDocuments: [],

  // Templates Initial State
  templates: [],
  loadingTemplates: false,
};

const removeDeletedDocuments = (
  documents: CertificationDocument[],
  deletedIds: number[]
): CertificationDocument[] => {
  if (!Array.isArray(documents) || documents.length === 0) {
    return [];
  }

  return documents
    .filter((doc) => !deletedIds.includes(doc.id))
    .map((doc) => {
      const upperChildren = removeDeletedDocuments(
        doc.upper_children_certification_documents || [],
        deletedIds
      );
      const lowerChildren = removeDeletedDocuments(
        doc.lower_children_certification_documents || [],
        deletedIds
      );
      return {
        ...doc,
        upper_children_certification_documents: upperChildren,
        lower_children_certification_documents: lowerChildren,
      };
    });
};

const fileManagerV3Slice = createSlice({
  name: 'fileManagerV3',
  initialState,
  reducers: {
    // Core state actions
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setIsInitialLoad: (state, action: PayloadAction<boolean>) => {
      state.isInitialLoad = action.payload;
    },
    // Data actions
    setCertificationDocuments: (
      state,
      action: PayloadAction<CertificationDocument[]>
    ) => {
      state.certificationDocuments = action.payload;
      // renderedDocuments is NOT automatically updated
      // Only updated explicitly via setRenderedDocuments or during initial load
    },

    updateDocumentOrders: (
      state,
      action: PayloadAction<Array<OrderDataItem>>
    ) => {
      // Update order-related fields for specific documents and restructure the tree
      const orderChanges = action.payload;

      // Build a map of parent changes to efficiently clean up old children arrays
      const parentChanges = new Map<number, number | null>();
      orderChanges.forEach((change) => {
        if (change.parent_id !== undefined) {
          parentChanges.set(change.id, change.parent_id);
        }
      });

      // Build a full documents array with updated properties
      const updatedDocs = orderChanges
        .map((change) => {
          // Find the existing document (could be top-level or nested)
          const findDoc = (
            docs: CertificationDocument[]
          ): CertificationDocument | null => {
            for (const doc of docs) {
              if (doc.id === change.id) {
                return doc;
              }
              const upperMatch = findDoc(
                doc.upper_children_certification_documents || []
              );
              if (upperMatch) {
                return upperMatch;
              }
              const lowerMatch = findDoc(
                doc.lower_children_certification_documents || []
              );
              if (lowerMatch) {
                return lowerMatch;
              }
            }
            return null;
          };

          const existingDoc = findDoc(state.renderedDocuments);
          if (!existingDoc) {
            return null;
          }

          // Clean up children arrays if any of them have been moved to a different parent
          const cleanChildrenArray = (
            children: CertificationDocument[]
          ): CertificationDocument[] => {
            return children.filter((child) => {
              // If this child is in the payload and has a new parent, exclude it
              const newParent = parentChanges.get(child.id);
              return !(newParent !== undefined && newParent !== existingDoc.id);
            });
          };

          // Return document with updated order properties and cleaned children arrays
          return {
            ...existingDoc,
            order: change.order,
            sub_order: change.sub_order ?? existingDoc.sub_order,
            parent:
              change.parent_id !== undefined
                ? change.parent_id
                : existingDoc.parent,
            position:
              change.position !== undefined
                ? change.position
                : existingDoc.position,
            upper_children_certification_documents: cleanChildrenArray(
              existingDoc.upper_children_certification_documents || []
            ),
            lower_children_certification_documents: cleanChildrenArray(
              existingDoc.lower_children_certification_documents || []
            ),
          };
        })
        .filter((doc): doc is CertificationDocument => doc !== null);

      // Sort documents to process parents before children
      // This prevents children from being removed when their parent is removed and re-inserted
      const sortedDocs = [...updatedDocs].sort((a, b) => {
        // If 'a' is parent of 'b', process 'a' first
        if (b.parent === a.id) {
          return -1;
        }
        // If 'b' is parent of 'a', process 'b' first
        if (a.parent === b.id) {
          return 1;
        }
        // Otherwise, process top-level documents (parent === null) before children
        if (a.parent == null && b.parent != null) {
          return -1;
        }
        if (b.parent == null && a.parent != null) {
          return 1;
        }
        // Same level, maintain relative order
        return 0;
      });

      // Use upsertDocumentsIntoTree to properly restructure the tree
      // This will remove each document from its old position and insert it at its new position
      state.certificationDocuments = upsertDocumentsIntoTree(
        state.certificationDocuments,
        sortedDocs
      );
      // Also update renderedDocuments to reflect the new order
      state.renderedDocuments = upsertDocumentsIntoTree(
        state.renderedDocuments,
        sortedDocs
      );
      state.renderedDocuments = applyDisplayOrder(state.renderedDocuments);
    },
    orderAndUpsertDocuments: (
      state,
      action: PayloadAction<{
        orderChanges: Array<OrderDataItem>;
        documentsToUpsert: CertificationDocument[];
      }>
    ) => {
      const { orderChanges, documentsToUpsert } = action.payload;
      if (orderChanges.length > 0) {
        const parentChanges = new Map<number, number | null>();
        orderChanges.forEach((change) => {
          if (change.parent_id !== undefined) {
            parentChanges.set(change.id, change.parent_id);
          }
        });
        const findDoc = (
          docs: CertificationDocument[],
          id: number
        ): CertificationDocument | null => {
          for (const doc of docs) {
            if (doc.id === id) {
              return doc;
            }
            const upperMatch = findDoc(
              doc.upper_children_certification_documents || [],
              id
            );
            if (upperMatch) {
              return upperMatch;
            }
            const lowerMatch = findDoc(
              doc.lower_children_certification_documents || [],
              id
            );
            if (lowerMatch) {
              return lowerMatch;
            }
          }
          return null;
        };
        const cleanChildrenArray = (
          children: CertificationDocument[],
          parentId: number
        ): CertificationDocument[] =>
          children.filter((child) => {
            const newParent = parentChanges.get(child.id);
            return !(newParent !== undefined && newParent !== parentId);
          });
        const updatedDocs = orderChanges
          .map((change) => {
            const existingDoc = findDoc(state.renderedDocuments, change.id);
            if (!existingDoc) {
              return null;
            }
            return {
              ...existingDoc,
              order: change.order,
              sub_order: change.sub_order ?? existingDoc.sub_order,
              parent:
                change.parent_id !== undefined
                  ? change.parent_id
                  : existingDoc.parent,
              position:
                change.position !== undefined
                  ? change.position
                  : existingDoc.position,
              upper_children_certification_documents: cleanChildrenArray(
                existingDoc.upper_children_certification_documents || [],
                existingDoc.id
              ),
              lower_children_certification_documents: cleanChildrenArray(
                existingDoc.lower_children_certification_documents || [],
                existingDoc.id
              ),
            };
          })
          .filter((doc): doc is CertificationDocument => doc !== null);
        const sortedDocs = [...updatedDocs].sort((a, b) => {
          if (b.parent === a.id) {
            return -1;
          }
          if (a.parent === b.id) {
            return 1;
          }
          if (a.parent == null && b.parent != null) {
            return -1;
          }
          if (b.parent == null && a.parent != null) {
            return 1;
          }
          return 0;
        });
        state.renderedDocuments = upsertDocumentsIntoTree(
          state.renderedDocuments,
          sortedDocs
        );
        state.certificationDocuments = upsertDocumentsIntoTree(
          state.certificationDocuments,
          sortedDocs
        );
      }
      if (documentsToUpsert.length > 0) {
        state.renderedDocuments = upsertDocumentsIntoTree(
          state.renderedDocuments,
          documentsToUpsert
        );
        state.certificationDocuments = upsertDocumentsIntoTree(
          state.certificationDocuments,
          documentsToUpsert
        );
      }
      state.renderedDocuments = applyDisplayOrder(state.renderedDocuments);
      state.certificationDocuments = applyDisplayOrder(
        state.certificationDocuments
      );
    },
    updateDocumentsHasOrderChanged: (
      state,
      action: PayloadAction<{
        documentIds: number[];
        hasOrderChanged: boolean;
      }>
    ) => {
      const { documentIds, hasOrderChanged } = action.payload;
      const idSet = new Set(documentIds);

      state.certificationDocuments = state.certificationDocuments.map((doc) => {
        if (idSet.has(doc.id)) {
          return {
            ...doc,
            has_order_changed: hasOrderChanged,
          };
        }
        return doc;
      });
      // Also update renderedDocuments to keep them in sync
      state.renderedDocuments = state.renderedDocuments.map((doc) => {
        if (idSet.has(doc.id)) {
          return {
            ...doc,
            has_order_changed: hasOrderChanged,
          };
        }
        return doc;
      });
      state.renderedDocuments = applyDisplayOrder(state.renderedDocuments);
    },
    setCertificationFile: (
      state,
      action: PayloadAction<CertificationFile | null>
    ) => {
      state.certificationFile = action.payload;
    },
    setHouseholdId: (state, action: PayloadAction<number | null>) => {
      state.householdId = action.payload;
    },
    setHouseholdMembers: (
      state,
      action: PayloadAction<HouseholdMemberOption[]>
    ) => {
      state.householdMembers = action.payload;
    },
    setSelectedPrograms: (state, action: PayloadAction<string[]>) => {
      state.selectedPrograms = action.payload;
    },
    setPermissions: (
      state,
      action: PayloadAction<{
        canViewBlockerDetails: boolean;
        canAccessFormDebug: boolean;
      }>
    ) => {
      state.canViewBlockerDetails = action.payload.canViewBlockerDetails;
      state.canAccessFormDebug = action.payload.canAccessFormDebug;
    },

    // Selection actions
    setSelectedDocs: (
      state,
      action: PayloadAction<CertificationDocument[]>
    ): void => {
      state.selectedDocs = action.payload;
    },
    addToSelection: (
      state,
      action: PayloadAction<CertificationDocument>
    ): void => {
      const doc = action.payload;
      if (!state.selectedDocs.find((d) => d.id === doc.id)) {
        state.selectedDocs.push(doc);
      }
    },
    removeFromSelection: (state, action: PayloadAction<number>): void => {
      const docId = action.payload;
      state.selectedDocs = state.selectedDocs.filter((doc) => doc.id !== docId);
    },
    removeMultipleFromSelection: (
      state,
      action: PayloadAction<number[]>
    ): void => {
      const idsToRemove = new Set(action.payload);
      state.selectedDocs = state.selectedDocs.filter(
        (doc) => !idsToRemove.has(doc.id)
      );
    },
    clearSelection: (state): void => {
      state.selectedDocs = [];
    },

    // Search and filtering actions
    setSearch: (state, action: PayloadAction<string | null>): void => {
      state.search = action.payload;
      state.showClearSearch = action.payload
        ? action.payload.length > 0
        : false;
    },
    setRenderedDocuments: (
      state,
      action: PayloadAction<CertificationDocument[]>
    ): void => {
      state.renderedDocuments = applyDisplayOrder(action.payload || []);
    },
    // SURGICAL UPDATE: Only updates fields on specific documents by ID
    // Does NOT rebuild tree, preserves position, preserves all unaffected documents
    updateRenderedDocumentsInPlace: (
      state,
      action: PayloadAction<Partial<CertificationDocument>[]>
    ): void => {
      const updates = action.payload;
      const updatesById = new Map(updates.map((u) => [u.id!, u]));

      const updateNode = (
        doc: CertificationDocument
      ): CertificationDocument => {
        const update = updatesById.get(doc.id);

        // Recursively check children for updates
        const hasUpdatedChildren =
          (doc.upper_children_certification_documents || []).some((c) =>
            updatesById.has(c.id)
          ) ||
          (doc.lower_children_certification_documents || []).some((c) =>
            updatesById.has(c.id)
          );

        // If this node and its children have no updates, return as-is (no clone)
        if (!update && !hasUpdatedChildren) {
          return doc;
        }

        // Only recurse into children if they have updates
        const updatedUpperChildren = hasUpdatedChildren
          ? (doc.upper_children_certification_documents || []).map(updateNode)
          : doc.upper_children_certification_documents;
        const updatedLowerChildren = hasUpdatedChildren
          ? (doc.lower_children_certification_documents || []).map(updateNode)
          : doc.lower_children_certification_documents;

        // Apply update to this node if it exists
        if (update) {
          const updateCopy = { ...update };
          // Remove undefined values from update object
          for (const key in updateCopy) {
            const value = updateCopy[key];
            if (value === undefined) {
              delete updateCopy[key];
            }
          }

          return {
            ...doc,
            ...updateCopy,
            upper_children_certification_documents: updatedUpperChildren,
            lower_children_certification_documents: updatedLowerChildren,
          };
        }

        // Just updated children
        return {
          ...doc,
          upper_children_certification_documents: updatedUpperChildren,
          lower_children_certification_documents: updatedLowerChildren,
        };
      };

      state.renderedDocuments = state.renderedDocuments.map(updateNode);
      state.renderedDocuments = applyDisplayOrder(state.renderedDocuments);
      // Apply the same updates to certificationDocuments
      state.certificationDocuments =
        state.certificationDocuments.map(updateNode);
      state.certificationDocuments = applyDisplayOrder(
        state.certificationDocuments
      );
    },
    // UPSERT: Add new documents or move existing documents to new positions
    // NOTE: This DOES rebuild the tree because documents are moving positions
    upsertRenderedDocuments: (
      state,
      action: PayloadAction<CertificationDocument[]>
    ): void => {
      state.renderedDocuments = upsertDocumentsIntoTree(
        state.renderedDocuments,
        action.payload
      );
      state.renderedDocuments = applyDisplayOrder(state.renderedDocuments);
      // Apply the same upsert to certificationDocuments
      state.certificationDocuments = upsertDocumentsIntoTree(
        state.certificationDocuments,
        action.payload
      );
      state.certificationDocuments = applyDisplayOrder(
        state.certificationDocuments
      );
    },
    // SURGICAL DELETE: Remove only specific documents by ID
    deleteRenderedDocuments: (state, action: PayloadAction<number[]>): void => {
      const idsToRemove = new Set(action.payload);
      const filterTree = (
        docs: CertificationDocument[]
      ): CertificationDocument[] => {
        return docs
          .filter((doc) => !idsToRemove.has(doc.id))
          .map((doc) => {
            const hasChildrenToRemove =
              (doc.upper_children_certification_documents || []).some((c) =>
                idsToRemove.has(c.id)
              ) ||
              (doc.lower_children_certification_documents || []).some((c) =>
                idsToRemove.has(c.id)
              );

            // If no children need removal, return as-is
            if (!hasChildrenToRemove) {
              return doc;
            }

            // Only clone if children need filtering
            return {
              ...doc,
              upper_children_certification_documents: filterTree(
                doc.upper_children_certification_documents || []
              ),
              lower_children_certification_documents: filterTree(
                doc.lower_children_certification_documents || []
              ),
            };
          });
      };
      state.renderedDocuments = filterTree(state.renderedDocuments);
      state.renderedDocuments = applyDisplayOrder(state.renderedDocuments);
      // Apply the same delete to certificationDocuments
      state.certificationDocuments = filterTree(state.certificationDocuments);
      state.certificationDocuments = applyDisplayOrder(
        state.certificationDocuments
      );
    },
    clearSearch: (state): void => {
      state.search = null;
      state.showClearSearch = false;
    },

    // Operations state actions
    setDownloading: (state, action: PayloadAction<boolean>): void => {
      state.downloading = action.payload;
    },
    setPreviewing: (state, action: PayloadAction<boolean>): void => {
      state.previewing = action.payload;
    },
    setPreviewOpen: (state, action: PayloadAction<boolean>): void => {
      state.previewOpen = action.payload;
    },
    setPreviewDocument: (
      state,
      action: PayloadAction<CertificationDocument | TemplateDocument | null>
    ): void => {
      state.previewDocument = action.payload;
    },
    setDeleting: (state, action: PayloadAction<boolean>): void => {
      state.deleting = action.payload;
    },
    setDeleteConfirmationOpen: (
      state,
      action: PayloadAction<boolean>
    ): void => {
      state.deleteConfirmationOpen = action.payload;
    },
    setDocumentsToDelete: (
      state,
      action: PayloadAction<CertificationDocument[]>
    ): void => {
      state.documentsToDelete = action.payload;
    },
    setIsOrdering: (state, action: PayloadAction<boolean>): void => {
      state.isOrdering = action.payload;
    },
    setIsSaving: (state, action: PayloadAction<boolean>): void => {
      state.isSaving = action.payload;
    },

    // Drag and drop actions

    // UI state actions
    setShowClearSearch: (state, action: PayloadAction<boolean>): void => {
      state.showClearSearch = action.payload;
    },
    setIsFullscreen: (state, action: PayloadAction<boolean>): void => {
      state.isFullscreen = action.payload;
    },

    setActiveDragId: (state, action: PayloadAction<string | null>): void => {
      state.activeDragId = action.payload;
    },
    setOverDropId: (state, action: PayloadAction<string | null>): void => {
      state.overDropId = action.payload;
    },
    // Deleted Documents Reducers
    setDeletedDocuments: (
      state,
      action: PayloadAction<CertificationDocument[]>
    ) => {
      state.deletedDocuments = action.payload;
    },

    removeFromDeletedDocuments: (state, action: PayloadAction<number[]>) => {
      const idsToRemove = new Set(action.payload);
      state.deletedDocuments = state.deletedDocuments.filter(
        (doc) => !idsToRemove.has(doc.id)
      );
    },

    addToDeletedDocuments: (
      state,
      action: PayloadAction<CertificationDocument[]>
    ) => {
      const existingIds = new Set(state.deletedDocuments.map((doc) => doc.id));
      const newDocs = action.payload.filter((doc) => !existingIds.has(doc.id));
      state.deletedDocuments = [...state.deletedDocuments, ...newDocs];
    },

    // Templates Reducers
    setTemplates: (state, action: PayloadAction<Array<TemplateDocument>>) => {
      state.templates = action.payload;
    },
    setLoadingTemplates: (state, action: PayloadAction<boolean>) => {
      state.loadingTemplates = action.payload;
    },

    // Reset action
    resetFileManagerV3: () => initialState,
  },
});

export const {
  setLoading,
  setIsInitialLoad,
  setCertificationDocuments,
  updateDocumentOrders,
  orderAndUpsertDocuments,
  updateDocumentsHasOrderChanged,
  setCertificationFile,
  setHouseholdId,
  setHouseholdMembers,
  setSelectedPrograms,
  setPermissions,
  setSelectedDocs,
  addToSelection,
  removeFromSelection,
  removeMultipleFromSelection,
  clearSelection,
  setSearch,
  setRenderedDocuments,
  updateRenderedDocumentsInPlace,
  upsertRenderedDocuments,
  deleteRenderedDocuments,
  clearSearch,
  setDownloading,
  setPreviewing,
  setPreviewOpen,
  setPreviewDocument,
  setDeleting,
  setDeleteConfirmationOpen,
  setDocumentsToDelete,
  setIsOrdering,
  setIsSaving,
  setShowClearSearch,
  setIsFullscreen,
  setActiveDragId,
  setOverDropId,
  setDeletedDocuments,
  removeFromDeletedDocuments,
  addToDeletedDocuments,
  setTemplates,
  setLoadingTemplates,
  resetFileManagerV3,
} = fileManagerV3Slice.actions;

// Async actions
export const getCertificationDocuments = (
  certificationId: number,
  enableLoading = true
) => {
  return async (dispatch: AppDispatch): Promise<void> => {
    if (enableLoading) {
      dispatch(setLoading(true));
    }

    try {
      const url = `certification/file_manager_v3/${certificationId}/`;

      const response = await axios.get(url);
      // Extract documents from the response and sort by order
      const documents = (response.data?.documents?.results || [])
        .map((doc: CertificationDocument) => ({
          ...doc,
        }))
        .sort(
          (a: CertificationDocument, b: CertificationDocument) =>
            a.order - b.order
        );

      // Extract deleted documents from the same response
      const deletedDocuments = (
        response.data?.deleted_documents?.results || []
      ).map((doc: CertificationDocument) => ({
        ...doc,
      }));

      // Store household ID if available
      const householdId = response.data?.household_id;
      if (householdId) {
        dispatch(setHouseholdId(householdId));
      }

      // Store permissions if available
      dispatch(
        setPermissions({
          canViewBlockerDetails:
            response.data?.can_view_blocker_details || false,
          canAccessFormDebug: response.data?.can_access_form_debug || false,
        })
      );

      dispatch(setCertificationDocuments(documents));
      dispatch(setRenderedDocuments(documents));
      dispatch(setDeletedDocuments(deletedDocuments));
      dispatch(setLoading(false));
    } catch (error) {
      console.error('Error fetching certification documents:', error);
      dispatch(setLoading(false));
    }
  };
};

const loadAndDispatchHouseholdMembers = async (
  query: string,
  dispatch: AppDispatch
): Promise<void> => {
  try {
    const householdResponse = await axios.get<HouseholdMemberResponse[]>(
      `household/household_member/?${query}&expand=mobile_phone,member_type`
    );

    // Members without a verified ULCM contact are kept but flagged
    // has_valid_communication=false so the assignment UI can show them as
    // disabled rather than silently hiding them (ENG-2320).

    const members = (householdResponse.data || [])
      .filter((m: HouseholdMemberResponse) => {
        if (m.is_helper) {
          return false;
        }
        const memberTypeName = (m.member_type?.name || '').toLowerCase();
        if (memberTypeName === 'non-household member emergency contact') {
          return false;
        }
        if (memberTypeName === 'dependent') {
          return false;
        }
        return true;
      })
      .map((m: HouseholdMemberResponse) => {
        return {
          id: m.id,
          // Phase 4.10.I — CertificationMember.id under HHM_V2_READ=ON.
          // Stable v2 key the template-add endpoint validates against.
          cert_member_id: m.cert_member_id,
          name: `${m.first_name} ${m.last_name}`,
          first_name: m.first_name,
          last_name: m.last_name,
          // ENG-2320: use the backend-computed ULCM verification flag.
          has_valid_communication: m.has_ulcm_verified_contact === true,
        };
      });

    dispatch(setHouseholdMembers(members));
  } catch (error) {
    console.error('Error loading household members:', error);
  }
};

export const fetchHouseholdMembers = (householdId: number) => {
  return async (dispatch: AppDispatch): Promise<void> => {
    await loadAndDispatchHouseholdMembers(`household=${householdId}`, dispatch);
  };
};

// HHM_V2_READ: v2-native certs have no legacy Household FK (household_id is
// NULL), so the dropdown can't be keyed on a legacy household id. Source
// members from the CertificationMember chain via the `?certification=` filter
// the household_member viewset's v2 list path supports.
export const fetchHouseholdMembersByCertification = (
  certificationId: number
) => {
  return async (dispatch: AppDispatch): Promise<void> => {
    await loadAndDispatchHouseholdMembers(
      `certification=${certificationId}`,
      dispatch
    );
  };
};

export const getCertificationFile = (certificationId: number) => {
  return async (dispatch: AppDispatch): Promise<void> => {
    try {
      const response = await axios.get(
        `certification/file/${certificationId}/?fields=id,title,is_generating,file,documents,has_new_logs`
      );
      dispatch(setCertificationFile(response.data as CertificationFile));
    } catch (error) {
      console.error('Error fetching certification file:', error);
      // Set null if no file exists
      dispatch(setCertificationFile(null));
    }
  };
};

export const deleteCertificationDocuments = (
  certificationId: number,
  docIds: number[]
) => {
  return async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<void> => {
    if (!Array.isArray(docIds) || docIds.length === 0) {
      return;
    }

    try {
      const { data } = await axios.post(
        `certification/file_manager_v3/${certificationId}/delete/`,
        { doc_ids: docIds }
      );

      const deletedIds: number[] = data?.deleted_doc_ids || [];
      const promotedDocs: CertificationDocument[] = data?.documents || [];

      const state = getState().fileManagerV3;

      // First, remove deleted documents
      const remainingDocuments = removeDeletedDocuments(
        state.certificationDocuments,
        deletedIds
      );

      // Then, upsert promoted documents (children that became top-level)
      // This properly removes them from their nested position and inserts them at top-level
      const mergedDocuments =
        promotedDocs.length > 0
          ? upsertDocumentsIntoTree(remainingDocuments, promotedDocs)
          : remainingDocuments;

      dispatch(setCertificationDocuments(mergedDocuments));
      dispatch(setRenderedDocuments(mergedDocuments));
      dispatch(clearSelection());
    } catch (error) {
      console.error('Error deleting certification documents:', error);
      throw error;
    }
  };
};

export const saveDocumentOrder = (
  certificationId: number,
  documents: CertificationDocument[]
) => {
  return async (dispatch: AppDispatch): Promise<void> => {
    try {
      dispatch(setIsSaving(true));

      const orderData = convertNestedToOrderData(documents);

      const payload = {
        order_data: orderData,
      };

      await axios.post(
        `certification/file_manager_v3/${certificationId}/change_order/`,
        payload
      );

      dispatch(setCertificationDocuments(documents));
      dispatch(setIsOrdering(false));
      dispatch(setIsSaving(false));
    } catch (error) {
      console.error('Error saving document order:', error);
      dispatch(setIsSaving(false));
      throw error;
    }
  };
};

export const getTemplates = (certificationId: number) => {
  return async (dispatch: AppDispatch): Promise<void> => {
    dispatch(setLoadingTemplates(true));

    try {
      const url = `questionnaire/form_library/form_template/?certification=${certificationId}`;
      const response = await axios.get(url);

      const templates = response.data || [];

      dispatch(setTemplates(templates));
    } catch (error) {
      console.error('Error fetching templates:', error);
      dispatch(setTemplates([]));
    } finally {
      dispatch(setLoadingTemplates(false));
    }
  };
};

export const downloadCertification = (
  certificationId: number,
  displayedDocuments: CertificationDocument[],
  onSuccess?: (fileUrl: string, password?: string) => void,
  onError?: (error: string) => void
) => {
  return async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<void> => {
    const state = getState().fileManagerV3;
    const { selectedDocs, renderedDocuments } = state;

    dispatch(setDownloading(true));

    try {
      let url = `certification/v3/download/${certificationId}/`;
      let docsToCalculateDownload: CertificationDocument[] = [];

      if (selectedDocs?.length > 0) {
        docsToCalculateDownload = selectedDocs;
      } else {
        docsToCalculateDownload = displayedDocuments;
      }

      // Emit the package in the arranged File Manager order, not the order the
      // rows happened to be checked (ENG-3852).
      const flattenedDocs = buildOrderedPackageDocuments(
        docsToCalculateDownload,
        renderedDocuments
      );

      if (flattenedDocs.length > 0) {
        const docsParam = flattenedDocs.map((doc) => doc.id).join(',');
        url += `?documents=${docsParam}`;
      }

      const response = await axios.get(url);

      if (response.data?.file) {
        dispatch(setDownloading(false));
        if (onSuccess) {
          onSuccess(response.data.file, response.data?.password);
        }
      } else {
        throw new Error('No file URL returned from server');
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        error?.message ||
        'Something went wrong downloading the file.';
      dispatch(setDownloading(false));
      if (onError) {
        onError(errorMessage);
      }
    }
  };
};

export const previewCertification = (
  certificationId: number,
  displayedDocuments: CertificationDocument[],
  onSuccess?: (
    fileUrl: string,
    documents?: CertificationDocument[],
    password?: string
  ) => void,
  onError?: (error: string) => void
) => {
  return async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<void> => {
    const state = getState().fileManagerV3;
    const { selectedDocs, renderedDocuments } = state;

    dispatch(setPreviewing(true));

    try {
      let url = `certification/v3/preview/${certificationId}/`;
      let docsToCalculatePreview: CertificationDocument[] = [];

      // Determine which documents to preview based on selection
      if (selectedDocs?.length > 0) {
        docsToCalculatePreview = selectedDocs;
      } else {
        docsToCalculatePreview = displayedDocuments;
      }

      // Emit the package in the arranged File Manager order, not the order the
      // rows happened to be checked (ENG-3852).
      const flattenedDocs = buildOrderedPackageDocuments(
        docsToCalculatePreview,
        renderedDocuments
      );

      if (flattenedDocs.length > 0) {
        const docsParam = flattenedDocs.map((doc) => doc.id).join(',');
        url += `?documents=${docsParam}`;
      }

      const response = await axios.get(url);

      if (response.data?.file) {
        // Set preview document and open modal
        dispatch(
          setPreviewDocument({
            id: 0,
            title: 'Certification Preview',
            file_url: response.data.file,
            category: 'preview',
            generation_status: 'success',
            preview_documents: response.data.documents || [], // Store documents with page info
          } as CertificationDocument)
        );
        dispatch(setPreviewOpen(true));
        dispatch(setPreviewing(false));
        if (onSuccess) {
          onSuccess(
            response.data.file,
            response.data.documents,
            response.data.password
          );
        }
      } else {
        throw new Error('No file URL returned from server');
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        error?.message ||
        'Something went wrong generating preview.';
      dispatch(setPreviewing(false));
      if (onError) {
        onError(errorMessage);
      }
    }
  };
};

export default fileManagerV3Slice.reducer;
